import { AlertTriangle, ArrowDownRight, ArrowUpRight } from 'lucide-react';

/**
 * Inline chat charts (CLE-276).
 *
 * Lightweight, dependency-free CSS/SVG chart primitives rendered inside Firmio
 * AI assistant messages. The chat Markdown renderer (chat/Markdown.tsx) detects
 * a fenced ```firmio-chart block carrying a JSON spec and hands it here, so the
 * same plain-string conversation turns work for both simulated Test Mode chats
 * and any future live responses — no HTML is ever injected (XSS-safe).
 *
 * All colours come from the dark-theme CSS tokens so the charts stay consistent
 * with the rest of the app.
 */

// --- Spec types ------------------------------------------------------------

interface KpiItem {
  label: string;
  value: string;
  /** Optional sub-line under the value (e.g. a delta). */
  delta?: string;
  /** Tint the value/accent: positive (green), negative (red), or neutral. */
  tone?: 'positive' | 'negative' | 'neutral';
}
interface KpiSpec {
  type: 'kpi';
  items: KpiItem[];
}

interface BarItem {
  label: string;
  value: number;
  /** Optional caption shown to the right of the bar (e.g. "12.400 EUR"). */
  caption?: string;
  tone?: 'positive' | 'negative' | 'neutral' | 'accent';
}
interface BarSpec {
  type: 'bar';
  title?: string;
  items: BarItem[];
}

interface StackSegment {
  label: string;
  value: number;
  colorIndex?: number;
}
interface StackRow {
  label: string;
  /** Optional flag (e.g. ">100% supra-alocat") shown in red after the name. */
  flag?: string;
  segments: StackSegment[];
}
interface StackSpec {
  type: 'stacked';
  title?: string;
  rows: StackRow[];
}

interface DonutSlice {
  label: string;
  value: number;
}
interface DonutSpec {
  type: 'donut';
  title?: string;
  slices: DonutSlice[];
}

interface TableRow {
  cells: string[];
  /** Row accent: highlights overdue (red) / upcoming (yellow) rows. */
  tone?: 'danger' | 'warning' | 'ok' | 'neutral';
}
interface TableSpec {
  type: 'table';
  title?: string;
  headers: string[];
  rows: TableRow[];
}

interface TimelineGroup {
  label: string;
  items: Array<{ label: string; value: string }>;
}
interface TimelineSpec {
  type: 'timeline';
  title?: string;
  groups: TimelineGroup[];
}

interface CompareSpec {
  type: 'compare';
  title?: string;
  /** e.g. capacity vs load: [{ label, value, max }]. */
  rows: Array<{ label: string; value: number; max: number; caption?: string }>;
}

interface AlertSpec {
  type: 'alert';
  tone?: 'danger' | 'warning' | 'info';
  text: string;
}

export type ChartSpec =
  | KpiSpec
  | BarSpec
  | StackSpec
  | DonutSpec
  | TableSpec
  | TimelineSpec
  | CompareSpec
  | AlertSpec;

// --- Shared palette (segment colours for stacked bars / donut) -------------

const SEGMENT_COLORS = [
  '#3b82f6', // blue
  '#8b5cf6', // purple
  '#22c55e', // green
  '#f59e0b', // amber
  '#ec4899', // pink
  '#06b6d4', // cyan
  '#f43f5e', // rose
  '#a3e635', // lime
];

function color(i: number): string {
  return SEGMENT_COLORS[i % SEGMENT_COLORS.length];
}

const TONE_TEXT: Record<string, string> = {
  positive: 'text-emerald-400',
  negative: 'text-red-400',
  neutral: 'text-foreground',
  accent: 'text-primary',
};

const TONE_BAR: Record<string, string> = {
  positive: 'bg-emerald-500',
  negative: 'bg-red-500',
  neutral: 'bg-primary',
  // A chart bar needs a saturated fill, so it can't ride on --accent (now a
  // pale hover surface). violet-500 is the brand purple these bars always were.
  accent: 'bg-violet-500',
};

// --- Individual chart renderers --------------------------------------------

function KpiCards({ spec }: { spec: KpiSpec }) {
  return (
    <div className="my-2 grid grid-cols-2 gap-2 sm:grid-cols-4">
      {spec.items.map((it, i) => (
        <div key={i} className="rounded-lg border border-border bg-muted/40 p-3">
          <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{it.label}</p>
          <p className={`mt-1 text-lg font-bold ${TONE_TEXT[it.tone ?? 'neutral']}`}>{it.value}</p>
          {it.delta && (
            <p
              className={`mt-0.5 flex items-center gap-0.5 text-xs ${
                it.tone === 'positive'
                  ? 'text-emerald-400'
                  : it.tone === 'negative'
                    ? 'text-red-400'
                    : 'text-muted-foreground'
              }`}
            >
              {it.tone === 'positive' && <ArrowUpRight size={12} />}
              {it.tone === 'negative' && <ArrowDownRight size={12} />}
              {it.delta}
            </p>
          )}
        </div>
      ))}
    </div>
  );
}

const RO_NUMBER = new Intl.NumberFormat('ro-RO', { maximumFractionDigits: 2 });

function BarChart({ spec }: { spec: BarSpec }) {
  // Bars are scaled by magnitude so a loss ("-420.538") draws as long as an
  // equal profit would, in red; the sign lives in the caption.
  const max = Math.max(...spec.items.map((i) => Math.abs(i.value)), 1);
  return (
    <div className="my-2 rounded-lg border border-border bg-muted/30 p-3">
      {spec.title && <p className="mb-2 text-xs font-semibold text-foreground">{spec.title}</p>}
      <div className="space-y-2">
        {spec.items.map((it, i) => {
          const tone = it.tone ?? (it.value < 0 ? 'negative' : 'neutral');
          const caption = it.caption ?? RO_NUMBER.format(it.value);
          return (
            <div key={i} className="flex items-center gap-2">
              <span
                className="w-28 shrink-0 truncate text-xs text-muted-foreground"
                title={it.label}
              >
                {it.label}
              </span>
              <div className="h-4 flex-1 overflow-hidden rounded bg-background">
                <div
                  className={`h-full rounded ${TONE_BAR[tone]}`}
                  style={{ width: `${Math.max((Math.abs(it.value) / max) * 100, 2)}%` }}
                />
              </div>
              <span
                className={`w-24 shrink-0 text-right text-xs font-medium tabular-nums ${
                  it.value < 0 ? 'text-red-400' : 'text-foreground'
                }`}
              >
                {caption}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function StackedBars({ spec }: { spec: StackSpec }) {
  // Collect a stable legend across all segments by label.
  const legend = new Map<string, number>();
  for (const row of spec.rows) {
    for (const seg of row.segments) {
      if (!legend.has(seg.label)) legend.set(seg.label, legend.size);
    }
  }
  return (
    <div className="my-2 rounded-lg border border-border bg-muted/30 p-3">
      {spec.title && <p className="mb-2 text-xs font-semibold text-foreground">{spec.title}</p>}
      <div className="space-y-3">
        {spec.rows.map((row, ri) => {
          const total = row.segments.reduce((s, seg) => s + seg.value, 0) || 1;
          return (
            <div key={ri}>
              <div className="mb-1 flex items-center gap-2">
                <span className="text-xs font-medium text-foreground">{row.label}</span>
                {row.flag && (
                  <span className="rounded bg-red-500/15 px-1.5 py-0.5 text-[10px] font-semibold text-red-400">
                    {row.flag}
                  </span>
                )}
              </div>
              <div className="flex h-5 w-full overflow-hidden rounded bg-background">
                {row.segments.map((seg, si) => (
                  <div
                    key={si}
                    className="h-full"
                    title={`${seg.label}: ${seg.value}%`}
                    style={{
                      width: `${(seg.value / total) * 100}%`,
                      background: color(seg.colorIndex ?? legend.get(seg.label) ?? si),
                    }}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>
      <div className="mt-3 flex flex-wrap gap-x-3 gap-y-1">
        {[...legend.entries()].map(([label, idx]) => (
          <span key={label} className="flex items-center gap-1 text-[11px] text-muted-foreground">
            <span className="h-2.5 w-2.5 rounded-sm" style={{ background: color(idx) }} />
            {label}
          </span>
        ))}
      </div>
    </div>
  );
}

function Donut({ spec }: { spec: DonutSpec }) {
  const total = spec.slices.reduce((s, sl) => s + sl.value, 0) || 1;
  const radius = 60;
  const circumference = 2 * Math.PI * radius;
  let offset = 0;
  const arcs = spec.slices.map((sl, i) => {
    const fraction = sl.value / total;
    const dash = fraction * circumference;
    const arc = {
      key: i,
      color: color(i),
      dashArray: `${dash} ${circumference - dash}`,
      dashOffset: -offset,
      label: sl.label,
      pct: Math.round(fraction * 100),
    };
    offset += dash;
    return arc;
  });
  return (
    <div className="my-2 flex flex-col items-center gap-4 rounded-lg border border-border bg-muted/30 p-3 sm:flex-row">
      {spec.title && (
        <p className="w-full text-xs font-semibold text-foreground sm:hidden">{spec.title}</p>
      )}
      <svg viewBox="0 0 160 160" className="h-32 w-32 shrink-0 -rotate-90">
        {arcs.map((a) => (
          <circle
            key={a.key}
            cx="80"
            cy="80"
            r={radius}
            fill="none"
            stroke={a.color}
            strokeWidth="20"
            strokeDasharray={a.dashArray}
            strokeDashoffset={a.dashOffset}
          />
        ))}
      </svg>
      <div className="min-w-0 flex-1">
        {spec.title && (
          <p className="mb-2 hidden text-xs font-semibold text-foreground sm:block">{spec.title}</p>
        )}
        <ul className="space-y-1">
          {arcs.map((a) => (
            <li key={a.key} className="flex items-center gap-2 text-xs text-muted-foreground">
              <span className="h-2.5 w-2.5 shrink-0 rounded-sm" style={{ background: a.color }} />
              <span className="min-w-0 flex-1 truncate text-foreground">{a.label}</span>
              <span className="shrink-0 font-medium">{a.pct}%</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

const ROW_TONE: Record<string, string> = {
  danger: 'bg-red-500/10 text-red-300',
  warning: 'bg-amber-500/10 text-amber-300',
  ok: 'bg-emerald-500/10 text-emerald-300',
  neutral: '',
};

function DataTable({ spec }: { spec: TableSpec }) {
  return (
    <div className="my-2 overflow-x-auto rounded-lg border border-border">
      {spec.title && (
        <p className="border-b border-border bg-muted/40 px-3 py-2 text-xs font-semibold text-foreground">
          {spec.title}
        </p>
      )}
      <table className="w-full border-collapse text-xs">
        <thead>
          <tr className="bg-muted/40">
            {spec.headers.map((h, i) => (
              <th
                key={i}
                className="whitespace-nowrap border-b border-border px-3 py-2 text-left font-semibold text-muted-foreground"
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {spec.rows.map((row, ri) => (
            <tr key={ri} className={`${ROW_TONE[row.tone ?? 'neutral']}`}>
              {row.cells.map((c, ci) => (
                <td
                  key={ci}
                  className="whitespace-nowrap border-b border-border/60 px-3 py-1.5 text-foreground"
                >
                  {c}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Timeline({ spec }: { spec: TimelineSpec }) {
  return (
    <div className="my-2 rounded-lg border border-border bg-muted/30 p-3">
      {spec.title && <p className="mb-2 text-xs font-semibold text-foreground">{spec.title}</p>}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {spec.groups.map((g, gi) => (
          <div key={gi} className="rounded-md border border-border bg-background p-2">
            <p className="mb-1.5 border-b border-border/60 pb-1 text-[11px] font-semibold uppercase tracking-wide text-primary">
              {g.label}
            </p>
            <ul className="space-y-1">
              {g.items.map((it, ii) => (
                <li key={ii} className="flex items-center justify-between gap-2 text-xs">
                  <span className="min-w-0 flex-1 truncate text-muted-foreground">{it.label}</span>
                  <span className="shrink-0 font-medium text-foreground">{it.value}</span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}

function CompareBars({ spec }: { spec: CompareSpec }) {
  return (
    <div className="my-2 rounded-lg border border-border bg-muted/30 p-3">
      {spec.title && <p className="mb-2 text-xs font-semibold text-foreground">{spec.title}</p>}
      <div className="space-y-2">
        {spec.rows.map((row, i) => {
          const over = row.value > row.max;
          const pct = Math.min((row.value / row.max) * 100, 100);
          const overPct = over ? Math.min(((row.value - row.max) / row.max) * 100, 100) : 0;
          return (
            <div key={i} className="flex items-center gap-2">
              <span className="w-28 shrink-0 truncate text-xs text-muted-foreground">
                {row.label}
              </span>
              <div className="relative h-4 flex-1 overflow-hidden rounded bg-background">
                {/* capacity marker (max) line */}
                <div className="flex h-full">
                  <div
                    className={`h-full ${over ? 'bg-red-500' : 'bg-emerald-500'}`}
                    style={{ width: `${pct}%` }}
                  />
                  {over && <div className="h-full bg-red-700" style={{ width: `${overPct}%` }} />}
                </div>
              </div>
              <span
                className={`w-24 shrink-0 text-right text-xs font-medium ${
                  over ? 'text-red-400' : 'text-foreground'
                }`}
              >
                {row.caption ?? `${row.value}/${row.max}`}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

const ALERT_TONE: Record<string, string> = {
  danger: 'border-red-500/40 bg-red-500/10 text-red-300',
  warning: 'border-amber-500/40 bg-amber-500/10 text-amber-300',
  info: 'border-primary/40 bg-primary/10 text-primary',
};

function AlertBanner({ spec }: { spec: AlertSpec }) {
  return (
    <div
      className={`my-2 flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium ${
        ALERT_TONE[spec.tone ?? 'info']
      }`}
    >
      <AlertTriangle size={16} className="shrink-0" />
      <span>{spec.text}</span>
    </div>
  );
}

// --- Dispatcher ------------------------------------------------------------

export function ChatChart({ spec }: { spec: ChartSpec }) {
  switch (spec.type) {
    case 'kpi':
      return <KpiCards spec={spec} />;
    case 'bar':
      return <BarChart spec={spec} />;
    case 'stacked':
      return <StackedBars spec={spec} />;
    case 'donut':
      return <Donut spec={spec} />;
    case 'table':
      return <DataTable spec={spec} />;
    case 'timeline':
      return <Timeline spec={spec} />;
    case 'compare':
      return <CompareBars spec={spec} />;
    case 'alert':
      return <AlertBanner spec={spec} />;
    default:
      return null;
  }
}

/** Parse a `firmio-chart` fenced block body into a ChartSpec (null on bad JSON). */
export function parseChartSpec(raw: string): ChartSpec | null {
  try {
    const spec = JSON.parse(raw) as ChartSpec;
    if (spec && typeof spec === 'object' && typeof spec.type === 'string') return spec;
  } catch {
    // Malformed spec — fall back to showing nothing rather than crashing.
  }
  return null;
}
